import { useState } from "react";

import { Button } from "common/components/ui/Button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "common/components/ui/Dialog";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
} from "common/components/ui/Drawer";
import ProfileImage from "common/components/ui/ProfileImage";
import RadioButton from "common/components/ui/RadioButton";
import { useSelectedWorkspaceContext } from "common/helpers/SelectedWorkspaceContext";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import useWorkspaceNavigate from "common/hooks/useWorkspaceNavigate";
import type { Lead } from "common/types";

export default function MessageLeadDialog({
  lead,
  isOpen,
  onClose,
}: {
  lead: Lead;
  isOpen: boolean;
  onClose: () => void;
}) {
  const isTabletOrDesktop = useTwBreakpoint("md");
  const { accounts } = useSelectedWorkspaceContext();
  const { relativeNavigate } = useWorkspaceNavigate();
  const [selectedAccountId, setSelectedAccountId] = useState<string>(null);

  const Component = isTabletOrDesktop ? Dialog : Drawer;
  const Content = isTabletOrDesktop ? DialogContent : DrawerContent;
  const Title = isTabletOrDesktop ? DialogTitle : DrawerTitle;
  const Description = isTabletOrDesktop ? DialogDescription : DrawerDescription;
  const Close = isTabletOrDesktop ? DialogClose : DrawerClose;

  return (
    <Component
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <Content className="md:max-h-[80%]">
        <Title>Choose sender</Title>

        <Description className="mb-8">
          Choose from which account you want to message this lead
        </Description>

        <div className="mb-4 flex flex-col gap-y-2.5 overflow-y-auto md:gap-y-4">
          {lead.lead_of.map((leadOfId) => {
            const {
              id,
              picture_url: pictureUrl,
              full_name: fullName,
              occupation,
            } = accounts.find((account) => account.id === leadOfId);

            return (
              <button
                key={id}
                className="flex items-center gap-x-2 rounded-3xl border border-black-200 p-3"
                type="button"
                onClick={() => setSelectedAccountId(id)}
              >
                <ProfileImage src={pictureUrl} size="lg" />

                <div className="flex flex-col gap-y-1 text-left">
                  <span className="text-md-bold">{fullName}</span>
                  <span className="line-clamp-1 text-xs text-black-500">
                    {occupation}
                  </span>
                </div>

                <RadioButton
                  checked={selectedAccountId === id}
                  isStyleOnly
                  className="ml-auto"
                />
              </button>
            );
          })}
        </div>

        <div className="flex gap-x-4">
          <Close asChild>
            <Button
              variant="secondary-black"
              className="flex-1"
              size={isTabletOrDesktop ? "md" : "lg"}
            >
              Cancel
            </Button>
          </Close>

          <Button
            className="flex-1"
            variant="default-black"
            size={isTabletOrDesktop ? "md" : "lg"}
            disabled={!selectedAccountId}
            onClick={() =>
              relativeNavigate("inbox", { state: { lead, selectedAccountId } })
            }
          >
            Go to chat
          </Button>
        </div>
      </Content>
    </Component>
  );
}
